import { DOCUMENT } from '@angular/common';
import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter, firstValueFrom, fromEvent, merge } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginResponse } from '../../models/auth.model';
import {
  BackOfficeRuntimeEnv,
  getBackOfficeRuntimeEnv,
  parseRuntimeBoolean,
  parseRuntimeNumber,
} from '../config/backoffice-runtime-env';
import { AuthService } from './auth.service';

const DEFAULT_IDLE_TIMEOUT_MINUTES = 30;
const DEFAULT_WARNING_BEFORE_LOGOUT_SECONDS = 60;
const KEEP_ALIVE_THROTTLE_MS = 5 * 60 * 1000;

interface BackOfficeIdleSessionConfig {
  enabled: boolean;
  keepAliveEnabled: boolean;
  idleTimeoutMs: number;
  warningBeforeLogoutSeconds: number;
}

export function resolveBackOfficeIdleSessionConfig(
  config: { backOfficeSessionTimeoutMs?: number },
  runtime: BackOfficeRuntimeEnv = getBackOfficeRuntimeEnv(),
): BackOfficeIdleSessionConfig {
  const runtimeIdleMinutes = parseRuntimeNumber(runtime.BACKOFFICE_IDLE_TIMEOUT_MINUTES);
  const configuredTimeoutMs = typeof config.backOfficeSessionTimeoutMs === 'number'
    && Number.isFinite(config.backOfficeSessionTimeoutMs)
    && config.backOfficeSessionTimeoutMs > 0
    ? config.backOfficeSessionTimeoutMs
    : null;
  const warningSeconds = parseRuntimeNumber(runtime.BACKOFFICE_WARNING_BEFORE_LOGOUT_SECONDS)
    ?? DEFAULT_WARNING_BEFORE_LOGOUT_SECONDS;

  return {
    enabled: parseRuntimeBoolean(runtime.BACKOFFICE_ENABLE_IDLE_TIMEOUT, true),
    keepAliveEnabled: parseRuntimeBoolean(runtime.BACKOFFICE_KEEP_ALIVE_ENABLED, true),
    idleTimeoutMs: runtimeIdleMinutes
      ? runtimeIdleMinutes * 60 * 1000
      : configuredTimeoutMs ?? DEFAULT_IDLE_TIMEOUT_MINUTES * 60 * 1000,
    warningBeforeLogoutSeconds: Math.max(1, Math.floor(warningSeconds)),
  };
}

export function resolveBackOfficeSessionTimeoutMs(config: { backOfficeSessionTimeoutMs?: number }): number {
  return resolveBackOfficeIdleSessionConfig(config).idleTimeoutMs;
}

export const AUTH_SESSION_INACTIVITY_TIMEOUT_MS = resolveBackOfficeSessionTimeoutMs(environment);

@Injectable({ providedIn: 'root' })
export class AuthSessionService implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router, { optional: true });
  private readonly config = resolveBackOfficeIdleSessionConfig(environment);

  readonly warningVisible = signal(false);
  readonly warningSecondsRemaining = signal(this.config.warningBeforeLogoutSeconds);
  readonly reauthenticationVisible = signal(false);
  readonly reauthenticationStep = signal<'credentials' | 'two-factor'>('credentials');
  readonly reauthenticationSubmitting = signal(false);
  readonly reauthenticationError = signal<string | null>(null);
  readonly reauthenticationReturnUrl = signal<string | null>(null);

  private activitySubscription: Subscription | null = null;
  private navigationSubscription: Subscription | null = null;
  private warningTimeout: ReturnType<typeof setTimeout> | null = null;
  private logoutTimeout: ReturnType<typeof setTimeout> | null = null;
  private warningCountdown: ReturnType<typeof setInterval> | null = null;
  private keepAliveInterval: ReturnType<typeof setInterval> | null = null;
  private longOperationKeepAlive: ReturnType<typeof setInterval> | null = null;
  private activeLongOperations = new Set<number>();
  private longOperationSequence = 0;
  private lastActivityAt = Date.now();
  private lastKeepAliveAt = Date.now();
  private keepAliveInFlight = false;
  private reauthenticationPromise: Promise<boolean> | null = null;
  private resolveReauthentication: ((result: boolean) => void) | null = null;
  private reauthenticationRequiresTwoFactorSetup = false;

  constructor() {
    this.startTracking();
  }

  ngOnDestroy(): void {
    this.stopTracking();
  }

  markUserActivity(_source = 'user'): void {
    if (!this.config.enabled) {
      return;
    }

    this.lastActivityAt = Date.now();
    this.warningVisible.set(false);
    this.warningSecondsRemaining.set(this.config.warningBeforeLogoutSeconds);
    this.scheduleIdleTimers();
    this.maybeSendKeepAlive();
  }

  continueSession(): void {
    this.markUserActivity('continue-session');
  }

  requestReauthentication(_reason = 'authorization-failure'): Promise<boolean> {
    if (this.reauthenticationPromise) {
      return this.reauthenticationPromise;
    }

    this.clearIdleTimers();
    this.warningVisible.set(false);
    this.reauthenticationStep.set('credentials');
    this.reauthenticationError.set(null);
    this.reauthenticationSubmitting.set(false);
    this.reauthenticationReturnUrl.set(this.router?.url ?? null);
    this.reauthenticationRequiresTwoFactorSetup = false;
    this.reauthenticationVisible.set(true);

    this.reauthenticationPromise = new Promise<boolean>((resolve) => {
      this.resolveReauthentication = resolve;
    });

    return this.reauthenticationPromise;
  }

  async submitReauthenticationCredentials(email: string, password: string): Promise<void> {
    if (this.reauthenticationSubmitting()) {
      return;
    }

    this.reauthenticationSubmitting.set(true);
    this.reauthenticationError.set(null);
    try {
      const challenge = await firstValueFrom(this.authService.login({ email, password }));
      await this.handleReauthenticationChallenge(challenge);
    } catch {
      this.reauthenticationError.set('Identifiants invalides ou service d authentification indisponible.');
    } finally {
      this.reauthenticationSubmitting.set(false);
    }
  }

  async submitReauthenticationTwoFactor(code: string): Promise<void> {
    if (this.reauthenticationSubmitting()) {
      return;
    }

    const temporaryToken = this.authService.temporaryToken();
    if (!temporaryToken) {
      this.reauthenticationStep.set('credentials');
      this.reauthenticationError.set('Jeton temporaire expire. Reconnectez-vous.');
      return;
    }

    this.reauthenticationSubmitting.set(true);
    this.reauthenticationError.set(null);
    try {
      const request = {
        temporaryToken,
        code,
      };
      await firstValueFrom(this.reauthenticationRequiresTwoFactorSetup
        ? this.authService.activateTwoFactorSetup(request)
        : this.authService.verifyTwoFactor(request));
      this.finishReauthentication(true);
    } catch {
      this.reauthenticationError.set('Code de verification invalide ou expire.');
    } finally {
      this.reauthenticationSubmitting.set(false);
    }
  }

  cancelReauthentication(): void {
    this.finishReauthentication(false);
    this.authService.logout({
      returnUrl: this.reauthenticationReturnUrl() ?? undefined,
      message: {
        summary: 'Session interrompue',
        detail: 'Reconnectez-vous pour reprendre votre travail.',
      },
    });
  }

  beginLongOperation(_source = 'api'): number {
    const operationId = ++this.longOperationSequence;
    this.activeLongOperations.add(operationId);
    this.clearIdleTimers();
    this.warningVisible.set(false);
    this.markUserActivity('long-operation-start');
    this.ensureLongOperationKeepAlive();
    return operationId;
  }

  endLongOperation(operationId: number | null | undefined): void {
    if (!operationId) {
      return;
    }

    this.activeLongOperations.delete(operationId);
    if (this.activeLongOperations.size === 0) {
      this.clearLongOperationKeepAlive();
      this.markUserActivity('long-operation-end');
    }
  }

  private startTracking(): void {
    if (!this.config.enabled) {
      return;
    }

    if (!this.activitySubscription) {
      const passiveOptions: AddEventListenerOptions = { passive: true };
      const activity$ = merge(
        fromEvent(this.document, 'mousemove', passiveOptions),
        fromEvent(this.document, 'mousedown', passiveOptions),
        fromEvent(this.document, 'click', passiveOptions),
        fromEvent(this.document, 'keydown'),
        fromEvent(this.document, 'scroll', passiveOptions),
        fromEvent(this.document, 'wheel', passiveOptions),
        fromEvent(this.document, 'touchstart', passiveOptions),
        fromEvent(this.document, 'submit'),
        fromEvent(this.document, 'input'),
        fromEvent(this.document, 'change'),
      );

      this.activitySubscription = activity$.subscribe(() => this.markUserActivity('dom'));
    }

    if (this.router && !this.navigationSubscription) {
      this.navigationSubscription = this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => this.markUserActivity('navigation'));
    }

    if (!this.keepAliveInterval && this.config.keepAliveEnabled) {
      this.keepAliveInterval = setInterval(() => {
        if (this.authService.isAuthenticated() && Date.now() - this.lastActivityAt < this.config.idleTimeoutMs) {
          this.maybeSendKeepAlive();
        }
      }, KEEP_ALIVE_THROTTLE_MS);
    }

    this.markUserActivity('page-open');
  }

  private scheduleIdleTimers(): void {
    this.clearIdleTimers();

    if (!this.config.enabled || !this.authService.isAuthenticated()) {
      return;
    }

    if (this.activeLongOperations.size > 0) {
      return;
    }

    const warningDelayMs = Math.max(
      0,
      this.config.idleTimeoutMs - this.config.warningBeforeLogoutSeconds * 1000,
    );

    this.warningTimeout = setTimeout(() => this.showExpiryWarning(), warningDelayMs);
    this.logoutTimeout = setTimeout(() => this.handleIdleTimeout(), this.config.idleTimeoutMs);
  }

  private showExpiryWarning(): void {
    if (!this.authService.isAuthenticated() || this.activeLongOperations.size > 0) {
      this.scheduleIdleTimers();
      return;
    }

    this.warningVisible.set(true);
    this.updateWarningCountdown();
    this.warningCountdown = setInterval(() => this.updateWarningCountdown(), 1000);
  }

  private updateWarningCountdown(): void {
    const expiresAt = this.lastActivityAt + this.config.idleTimeoutMs;
    const remainingSeconds = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
    this.warningSecondsRemaining.set(remainingSeconds);
  }

  private handleIdleTimeout(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (this.activeLongOperations.size > 0) {
      this.markUserActivity('long-operation-active');
      return;
    }

    const idleForMs = Date.now() - this.lastActivityAt;
    if (idleForMs < this.config.idleTimeoutMs) {
      this.scheduleIdleTimers();
      return;
    }

    this.authService.logoutDueToInactivity();
  }

  private maybeSendKeepAlive(): void {
    if (!this.config.keepAliveEnabled || this.keepAliveInFlight || !this.authService.isAuthenticated()) {
      return;
    }

    const now = Date.now();
    if (now - this.lastKeepAliveAt < KEEP_ALIVE_THROTTLE_MS) {
      return;
    }

    this.lastKeepAliveAt = now;
    void this.sendKeepAlive();
  }

  private async sendKeepAlive(): Promise<void> {
    const token = this.authService.token();
    const backendBaseUrl = getBackOfficeRuntimeEnv().BACKEND_BASE_URL?.replace(/\/+$/, '');
    if (!token || !backendBaseUrl) {
      return;
    }

    this.keepAliveInFlight = true;
    try {
      const response = await fetch(`${backendBaseUrl}/api/admin/session/keep-alive`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        return;
      }

      const body = await response.json() as { data?: { accessToken?: string } };
      const refreshedToken = body.data?.accessToken;
      if (refreshedToken) {
        this.authService.replaceAccessToken(refreshedToken);
      }
    } catch {
      // A failed keep-alive must not log out an active operator; the next API call will still enforce JWT validity.
    } finally {
      this.keepAliveInFlight = false;
    }
  }

  private async handleReauthenticationChallenge(challenge: LoginResponse): Promise<void> {
    this.reauthenticationRequiresTwoFactorSetup = Boolean(challenge.requiresTwoFactorSetup);
    if (challenge.requiresTwoFactorSetup) {
      await firstValueFrom(this.authService.setupTwoFactor({
        temporaryToken: challenge.temporaryToken,
        code: '',
      }));
    }
    this.reauthenticationStep.set('two-factor');
  }

  private finishReauthentication(success: boolean): void {
    const resolve = this.resolveReauthentication;
    this.reauthenticationVisible.set(false);
    this.reauthenticationSubmitting.set(false);
    this.reauthenticationError.set(null);
    this.warningVisible.set(false);
    this.resolveReauthentication = null;
    this.reauthenticationPromise = null;
    this.reauthenticationRequiresTwoFactorSetup = false;
    if (success) {
      this.markUserActivity('reauthenticated');
    }
    resolve?.(success);
  }

  private ensureLongOperationKeepAlive(): void {
    if (this.longOperationKeepAlive || !this.config.keepAliveEnabled) {
      return;
    }

    this.longOperationKeepAlive = setInterval(() => {
      if (this.activeLongOperations.size > 0) {
        this.maybeSendKeepAlive();
        return;
      }
      this.clearLongOperationKeepAlive();
    }, KEEP_ALIVE_THROTTLE_MS);
  }

  private clearIdleTimers(): void {
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }
    if (this.logoutTimeout) {
      clearTimeout(this.logoutTimeout);
      this.logoutTimeout = null;
    }
    if (this.warningCountdown) {
      clearInterval(this.warningCountdown);
      this.warningCountdown = null;
    }
  }

  private clearKeepAliveInterval(): void {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  private clearLongOperationKeepAlive(): void {
    if (this.longOperationKeepAlive) {
      clearInterval(this.longOperationKeepAlive);
      this.longOperationKeepAlive = null;
    }
  }

  private stopTracking(): void {
    this.activitySubscription?.unsubscribe();
    this.activitySubscription = null;
    this.navigationSubscription?.unsubscribe();
    this.navigationSubscription = null;
    this.clearIdleTimers();
    this.clearKeepAliveInterval();
    this.clearLongOperationKeepAlive();
  }
}
